import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Check, X, Loader, AlertCircle, FolderOpen } from "lucide-react";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
};

const ST_COLOR = {
  Activo:     { color:"#0284c7", bg:"#e0f2fe" },
  Completado: { color:C.ok,      bg:C.okL     },
  Pausado:    { color:"#f59e0b", bg:"#fef3c7" },
  Cancelado:  { color:C.err,     bg:C.errL    },
};

export default function AsignarTareasModal({ cotizacion, onClose }) {
  const [proyectos,    setProyectos]    = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [toast,        setToast]        = useState(null);
  const [seleccionadas, setSeleccionadas] = useState(
    () => new Set(cotizacion.items.map((_, idx) => idx))
  );

  const toggleItem = (idx) => {
    setSeleccionadas(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const notify = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("proyectos")
        .select("id, nombre, cliente, estado")
        .order("created_at", { ascending: false });
      if (error) notify("Error al cargar proyectos", "err");
      else setProyectos(data || []);
      setLoading(false);
    })();
  }, []);

  const confirmar = async () => {
    if (!seleccionado) return;
    setSaving(true);

    const itemsAInsertar = cotizacion.items
      .filter((_, idx) => seleccionadas.has(idx))
      .map((item, i) => ({
        proyecto_id:     seleccionado.id,
        cotizacion_id:   cotizacion.id,
        descripcion:     item.desc,
        tipo:            item.tipo,
        horas_estimadas: item.horas,
        estado:          "Por hacer",
        orden:           i + 1,
      }));

    const { error } = await supabase.from("tareas").insert(itemsAInsertar);

    if (error) {
      notify("Error al asignar las tareas", "err");
      console.error(error);
      setSaving(false);
      return;
    }

    const { data: projData } = await supabase
      .from("proyectos").select("cotizaciones_ids").eq("id", seleccionado.id).single();
    const ids = [...new Set([...(projData?.cotizaciones_ids || []), cotizacion.id])];
    await supabase.from("proyectos")
      .update({ cotizacion_id: cotizacion.id, cotizaciones_ids: ids })
      .eq("id", seleccionado.id);

    notify(`✓ ${seleccionadas.size} tareas asignadas a ${seleccionado.nombre}`);
    setTimeout(() => onClose(), 1200);
  };

  const items = cotizacion.items || [];

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9990,
      background:"rgba(15,23,42,0.5)", backdropFilter:"blur(3px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"24px 16px",
    }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spinning{animation:spin 1s linear infinite}
      `}</style>

      {toast && (
        <div style={{
          position:"fixed", top:20, right:20, zIndex:9999,
          display:"flex", alignItems:"center", gap:8,
          background:C.white,
          border:`1px solid ${toast.type==="err"?C.err:C.ok}40`,
          borderLeft:`3px solid ${toast.type==="err"?C.err:C.ok}`,
          borderRadius:8, padding:"11px 16px", fontSize:14,
          color:toast.type==="err"?C.err:C.ok,
          boxShadow:"0 4px 16px #00000014",
        }}>
          {toast.type==="err" ? <AlertCircle size={15}/> : <Check size={15}/>}
          {toast.msg}
        </div>
      )}

      <div style={{
        background:C.white, borderRadius:16, width:"100%", maxWidth:560,
        boxShadow:"0 24px 80px #00000022", border:`1px solid ${C.border}`,
        animation:"fadeUp .2s ease", fontFamily:"'DM Sans',sans-serif",
      }}>

        {/* Header */}
        <div style={{
          padding:"20px 24px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between",
        }}>
          <div style={{display:"flex", alignItems:"center", gap:12}}>
            <div style={{width:36, height:36, borderRadius:9, background:C.accentL,
              display:"flex", alignItems:"center", justifyContent:"center"}}>
              <FolderOpen size={18} color={C.accent}/>
            </div>
            <div>
              <div style={{fontWeight:700, fontSize:16, color:C.text}}>
                Asignar tareas a proyecto
              </div>
              <div style={{fontSize:12, color:C.sub, marginTop:2}}>
                Desde cotización{" "}
                <strong style={{color:C.text}}>{cotizacion.id}</strong>
                {" · "}{cotizacion.cliente}
              </div>
            </div>
          </div>
          <button onClick={onClose}
            style={{background:"transparent", border:"none", cursor:"pointer",
              color:C.light, padding:4, borderRadius:6, display:"flex"}}
            onMouseEnter={e => e.currentTarget.style.color = C.text}
            onMouseLeave={e => e.currentTarget.style.color = C.light}>
            <X size={18}/>
          </button>
        </div>

        <div style={{padding:"20px 24px"}}>

          {/* Lista proyectos */}
          <div style={{fontSize:12, fontWeight:600, color:C.sub,
            letterSpacing:.4, textTransform:"uppercase", marginBottom:8}}>
            Seleccionar proyecto
          </div>

          <div style={{
            maxHeight:280, overflowY:"auto", borderRadius:10,
            border:`1px solid ${C.border}`,
          }}>
            {loading && (
              <div style={{padding:"24px", textAlign:"center", color:C.sub,
                fontSize:13, display:"flex", alignItems:"center",
                justifyContent:"center", gap:8}}>
                <Loader size={15} className="spinning"/> Cargando proyectos...
              </div>
            )}

            {!loading && proyectos.length === 0 && (
              <div style={{padding:"24px", textAlign:"center",
                fontSize:13, color:C.light}}>
                No hay proyectos disponibles
              </div>
            )}

            {!loading && proyectos.map((p, idx) => {
              const selected = seleccionado?.id === p.id;
              const stStyle  = ST_COLOR[p.estado] || { color:C.sub, bg:C.bg };
              return (
                <div key={p.id}
                  onClick={() => setSeleccionado(p)}
                  style={{
                    display:"flex", alignItems:"center",
                    justifyContent:"space-between",
                    padding:"11px 14px", cursor:"pointer",
                    borderBottom: idx < proyectos.length - 1
                      ? `1px solid ${C.border}` : "none",
                    background: selected ? C.accentL : C.white,
                    border: selected
                      ? `1.5px solid ${C.accent}`
                      : `1.5px solid transparent`,
                    borderRadius: idx === 0 ? "10px 10px 0 0"
                      : idx === proyectos.length - 1 ? "0 0 10px 10px" : 0,
                    transition:"background .1s",
                  }}
                  onMouseEnter={e => {
                    if (!selected) e.currentTarget.style.background = C.bg;
                  }}
                  onMouseLeave={e => {
                    if (!selected) e.currentTarget.style.background = C.white;
                  }}>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:600, fontSize:14, color:C.text,
                      whiteSpace:"nowrap", overflow:"hidden",
                      textOverflow:"ellipsis"}}>
                      {p.nombre}
                    </div>
                    <div style={{fontSize:12, color:C.sub, marginTop:2}}>
                      {p.cliente}
                    </div>
                  </div>
                  <div style={{display:"flex", alignItems:"center", gap:8,
                    flexShrink:0, marginLeft:12}}>
                    <span style={{
                      padding:"2px 9px", borderRadius:20, fontSize:11,
                      fontWeight:500, color:stStyle.color,
                      background:stStyle.bg,
                    }}>
                      {p.estado || "—"}
                    </span>
                    {selected && <Check size={14} color={C.accent}/>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tareas a crear */}
          <div style={{marginTop:18}}>
            <div style={{fontSize:12, fontWeight:600, color:C.sub,
              letterSpacing:.4, textTransform:"uppercase", marginBottom:8}}>
              Tareas a crear
            </div>
            <div style={{
              background:C.bg, border:`1px solid ${C.border}`,
              borderRadius:10, padding:"12px 14px",
            }}>
              {items.map((item, idx) => {
                const checked = seleccionadas.has(idx);
                return (
                  <div key={idx}
                    onClick={() => toggleItem(idx)}
                    style={{
                      display:"flex", alignItems:"center", gap:8,
                      padding:"6px 8px",
                      borderBottom: idx < items.length - 1
                        ? `1px solid ${C.border}` : "none",
                      borderRadius:6,
                      fontSize:13,
                      cursor:"pointer",
                      background: checked ? "#f0fdf4" : C.white,
                      border: checked
                        ? `1px solid #bbf7d0`
                        : `1px solid ${C.border}`,
                      marginBottom: idx < items.length - 1 ? 4 : 0,
                      opacity: checked ? 1 : 0.45,
                      transition:"background .1s, opacity .1s",
                    }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleItem(idx)}
                      onClick={e => e.stopPropagation()}
                      style={{cursor:"pointer", flexShrink:0, accentColor:C.accent}}
                    />
                    <span style={{flex:1, color:C.text}}>{item.desc}</span>
                    <span style={{
                      fontSize:11, color:C.sub, background:C.white,
                      border:`1px solid ${C.border}`, borderRadius:5,
                      padding:"1px 7px", flexShrink:0,
                    }}>
                      {item.tipo}
                    </span>
                    <span style={{fontSize:12, color:C.sub, flexShrink:0}}>
                      {item.horas}h
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:12, color: seleccionadas.size === 0 ? C.err : C.sub, marginTop:7}}>
              Se crearán <strong>{seleccionadas.size}</strong> de {items.length} tarea{items.length !== 1 ? "s" : ""} en estado 'Por hacer'
            </div>
          </div>

          {/* Acciones */}
          <div style={{display:"flex", gap:10, justifyContent:"flex-end",
            marginTop:22}}>
            <button onClick={onClose}
              style={{padding:"9px 22px", borderRadius:8,
                border:`1px solid ${C.border}`, background:"transparent",
                color:C.sub, fontSize:14, cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>
              Cancelar
            </button>
            <button onClick={confirmar}
              disabled={!seleccionado || seleccionadas.size === 0 || saving}
              style={{
                display:"flex", alignItems:"center", gap:7,
                padding:"9px 24px", borderRadius:8, border:"none",
                background: !seleccionado || seleccionadas.size === 0 || saving ? "#94a3b8" : C.accent,
                color:"#fff", fontWeight:600, fontSize:14,
                cursor: !seleccionado || seleccionadas.size === 0 || saving ? "not-allowed" : "pointer",
                fontFamily:"'DM Sans',sans-serif",
                boxShadow: !seleccionado || seleccionadas.size === 0 || saving
                  ? "none" : `0 4px 12px ${C.accent}40`,
                transition:"background .15s",
              }}
              onMouseEnter={e => {
                if (seleccionado && seleccionadas.size > 0 && !saving)
                  e.currentTarget.style.background = "#1a9fd4";
              }}
              onMouseLeave={e => {
                if (seleccionado && seleccionadas.size > 0 && !saving)
                  e.currentTarget.style.background = C.accent;
              }}>
              {saving
                ? <><Loader size={14} className="spinning"/>Asignando...</>
                : <><FolderOpen size={15}/>Asignar tareas</>}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
